import { css } from '@emotion/react';
import Star from 'practice_room/components/shared/stat-with-stars/star';
import { MutableRefObject, useCallback, useEffect, useRef } from 'react';
import {
  halfSpacing,
  standardSpacing,
} from 'styles/global_defaults/scaffolding';
import { random } from 'underscore';
import { tooltipZIndex } from './constants';

type StarsAmount = {
  min: number;
  max: number;
};

type StarsConfettiProps = {
  children: React.ReactElement;
  showLeft?: boolean;
  showRight?: boolean;
  showCenter?: boolean;
  starsAmount?: StarsAmount;
};

type StarsElementProps = {
  show: boolean;
  className: string;
  refElement: MutableRefObject<HTMLDivElement | null>;
};

const containerStyles = css`
  width: 100%;
  position: relative;
`;

const styles = css`
  pointer-events: none;
  position: absolute;
  top: -${halfSpacing}px;
  width: ${standardSpacing}%;
  height: 100%;
  z-index: ${tooltipZIndex + 1};

  &.show-left {
    left: -${halfSpacing}px;
  }

  &.show-right {
    right: -${halfSpacing}px;
  }

  &.show-center {
    top: -${halfSpacing}px;
    left: ${halfSpacing}px;
    right: ${halfSpacing}px;
    height: 160%;
    width: auto;
  }
  /* Styles associated with each star */
  .star {
    position: absolute;
    top: 0px;
    opacity: 0;
    font-size: 0px; /* To position svg correctly */

    svg {
      transform: translateZ(0px);
    }

    .close-star {
      transform: rotate(45deg);
    }
  }
`;

const STAR_MAX_SIZE = 10;
const STAR_DEFAULT_RANDOM_MIN = 35;
const STAR_DEFAULT_RANDOM_MAX = 45;

// Insert stars to the element given
const insertStars = (element: HTMLDivElement, starsAmount: StarsAmount) => {
  const { min = STAR_DEFAULT_RANDOM_MIN, max = STAR_DEFAULT_RANDOM_MAX } = starsAmount || {};
  for (let i = 0; i < random(min, max); i += 1) {
    // eslint-disable-next-line no-new
    new Star(element, STAR_MAX_SIZE);
  }
};

const StarsElement = ({ show, className, refElement }: StarsElementProps) => {
  if (show) {
    return (
      <div
        css={styles}
        ref={refElement}
        className={`star-item d-flex align-items-center text-success ${className}`}
      />
    );
  }
  return null;
};

const StarsConfetti = ({
  children,
  showLeft = false,
  showRight = false,
  showCenter = false,
  starsAmount,
}: StarsConfettiProps) => {
  const leftElement = useRef<HTMLDivElement | null>(null);
  const rightElement = useRef<HTMLDivElement | null>(null);
  const centerElement = useRef<HTMLDivElement | null>(null);

  const showStars = useCallback((element) => {
    if (element?.offsetParent) {
      insertStars(element, starsAmount);
    }
  }, []);

  useEffect(() => {
    setTimeout(() => {
      if (showLeft) {
        showStars(leftElement.current);
      }
      if (showRight) {
        showStars(rightElement.current);
      }
      if (showCenter) {
        showStars(centerElement.current);
      }
    }, 100);
  }, [showLeft, showRight, showCenter, showStars]);

  return (
    <div css={containerStyles}>
      <StarsElement show={showLeft} className='show-left' refElement={leftElement} />
      {children}
      <StarsElement show={showCenter} className='show-center' refElement={centerElement} />
      <StarsElement show={showRight} className='show-right' refElement={rightElement} />
    </div>
  );
};

export default StarsConfetti;
